/**
 * Подписка на уведомления в браузере (docs/05-integrations.md).
 *
 * Разрешение спрашивается только по явному действию на экране настроек,
 * а не при первом входе. Браузер даёт разрешение один раз на устройство
 * и на источник, поэтому подписок у человека может быть несколько.
 */

export interface PushSubscriptionPayload {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent: string;
}

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!pushSupported()) return 'unsupported';
  return Notification.permission;
}

/** Открытый ключ приходит с сервера в base64url, а браузеру нужен массив байтов. */
function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function encodeKey(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/sw.js');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js');
}

export class PushError extends Error {}

/**
 * Запросить разрешение и подписаться. Возвращает то, что нужно серверу
 * для отправки: адрес и два ключа шифрования.
 */
export async function subscribeToPush(publicKey: string): Promise<PushSubscriptionPayload> {
  if (!pushSupported()) {
    throw new PushError('Этот браузер не умеет показывать уведомления');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError(
      'Браузер не дал разрешения. Его можно выдать в настройках сайта и попробовать снова',
    );
  }

  const worker = await registration();
  await navigator.serviceWorker.ready;

  const existing = await worker.pushManager.getSubscription();
  // Прежняя подписка могла быть выдана на другой ключ: она уже не работает.
  if (existing) await existing.unsubscribe();

  const subscription = await worker.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  return {
    endpoint: subscription.endpoint,
    p256dh: encodeKey(subscription.getKey('p256dh')),
    auth: encodeKey(subscription.getKey('auth')),
    userAgent: navigator.userAgent.slice(0, 300),
  };
}

/** Отписаться на этом устройстве. Возвращает адрес, чтобы сервер удалил запись. */
export async function unsubscribeFromPush(): Promise<string | null> {
  if (!pushSupported()) return null;
  const worker = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!worker) return null;

  const subscription = await worker.pushManager.getSubscription();
  if (!subscription) return null;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  return endpoint;
}

/** Адрес подписки этого устройства: по нему видно, какое устройство текущее. */
export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported()) return null;
  const worker = await navigator.serviceWorker.getRegistration('/sw.js');
  if (!worker) return null;
  const subscription = await worker.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
}
